import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type ChecklistItemInput,
  ChecklistItemsInput,
  type RecurrenceFreq,
  type RecurrenceInput,
  RecurrenceRule,
  type RecurrenceView,
  TaskPriority,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogApply } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { TaskChecklistEditor } from '../../../components/ui/task-checklist-editor';
import { cn } from '../../../lib/utils';
import { displayDate, today } from '../../../lib/date';
import { errorMessage } from '../../../lib/error-message';
import { randomUuid } from '../../../lib/uuid';
import { recurrenceApi, recurrenceKeys, taskFormApi, taskFormKeys } from '../api';
import { WEEKDAY_ORDER, previewDates } from '../recurrence';
import { AssigneePicker } from './assignee-picker';

const FREQS: readonly RecurrenceFreq[] = ['daily', 'weekly', 'monthly', 'interval'];

const FIELD = 'h-9 rounded-DEFAULT border border-input bg-background px-2 text-sm';

/**
 * Tạo / sửa một mẫu việc định kỳ — APM-REC-b.
 *
 * Sửa mẫu chỉ đổi các lần **sau**: task đã sinh là task thật, đã có người làm
 * dở, và viết đè lên nó là mất lịch sử của đúng lần ấy. Câu nhắc ở chân form
 * nói ra điều này.
 *
 * Ô "3 lần tới" tính ngay trên trình duyệt bằng `scheduledDates` của contracts
 * — cùng hàm API dùng để sinh task, nên thứ người dùng thấy trước là thứ sẽ xảy
 * ra (trừ dời ngày nghỉ, xem `previewDates`).
 */
export function RecurrenceFormDialog({
  projectId,
  recurrence,
  onClose,
}: {
  readonly projectId: string;
  /** Bỏ trống = tạo mẫu mới. */
  readonly recurrence?: RecurrenceView;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const editing = recurrence !== undefined;

  const types = useQuery({
    queryKey: taskFormKeys.taskTypes(projectId),
    queryFn: () => taskFormApi.taskTypes(projectId),
  });
  const members = useQuery({
    queryKey: taskFormKeys.members(projectId),
    queryFn: () => taskFormApi.members(projectId),
  });

  const [title, setTitle] = useState(recurrence?.title ?? '');
  const [taskTypeId, setTaskTypeId] = useState(recurrence?.taskTypeId ?? '');
  const [assigneeId, setAssigneeId] = useState(recurrence?.assigneeId ?? '');
  const [priority, setPriority] = useState<TaskPriority>(recurrence?.priority ?? 'medium');
  const [estimate, setEstimate] = useState(
    recurrence?.estimateHours == null ? '' : String(recurrence.estimateHours),
  );
  const [checklist, setChecklist] = useState<ChecklistItemInput[] | null>(
    recurrence && recurrence.checklist.length > 0 ? recurrence.checklist : null,
  );
  const [freq, setFreq] = useState<RecurrenceFreq>(recurrence?.rule.freq ?? 'weekly');
  const [weekdays, setWeekdays] = useState<number[]>(recurrence?.rule.weekdays ?? [1]);
  const [monthDay, setMonthDay] = useState(String(recurrence?.rule.monthDay ?? 1));
  const [intervalDays, setIntervalDays] = useState(String(recurrence?.rule.intervalDays ?? 14));
  const [startDate, setStartDate] = useState(recurrence?.rule.startDate ?? today());
  const [endDate, setEndDate] = useState(recurrence?.rule.endDate ?? '');
  const [leadDays, setLeadDays] = useState(String(recurrence?.leadDays ?? 0));

  // Loại việc mặc định: loại đầu tiên đã gắn với dự án, khi danh sách về.
  const effectiveTypeId = taskTypeId || types.data?.[0]?.id || '';

  const rule = RecurrenceRule.safeParse({
    freq,
    weekdays: freq === 'weekly' ? weekdays : [],
    monthDay: freq === 'monthly' ? Number(monthDay) : null,
    intervalDays: freq === 'interval' ? Number(intervalDays) : null,
    startDate,
    endDate: endDate || null,
  });
  const preview = rule.success ? previewDates(rule.data, today()) : [];
  const checklistValid = checklist === null || ChecklistItemsInput.safeParse(checklist).success;
  const lead = Number(leadDays);
  const leadValid = Number.isInteger(lead) && lead >= 0 && lead <= 60;

  const canSave =
    title.trim() !== '' && effectiveTypeId !== '' && rule.success && checklistValid && leadValid;

  const save = useMutation({
    mutationFn: () => {
      const body: RecurrenceInput = {
        title: title.trim(),
        description: recurrence?.description ?? null,
        taskTypeId: effectiveTypeId,
        assigneeId: assigneeId || null,
        priority,
        estimateHours: estimate === '' ? null : Number(estimate),
        checklist: checklist ?? [],
        rule: rule.data!,
        leadDays: lead,
      };
      return editing
        ? recurrenceApi.update(projectId, recurrence.id, body)
        : recurrenceApi.create(projectId, body);
    },
    onSuccess: async (list) => {
      queryClient.setQueryData(recurrenceKeys.list(projectId), list);
      // Tạo mẫu xong API sinh ngay lần đầu nếu đã tới cửa sổ — board phải thấy nó.
      await queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      toast.success(editing ? t('recurrence.updated') : t('recurrence.created'));
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const toggleWeekday = (day: number): void =>
    setWeekdays((days) =>
      days.includes(day) ? days.filter((value) => value !== day) : [...days, day],
    );
  const formId = useId();

  return (
    <Dialog
      label={editing ? t('recurrence.editTitle') : t('recurrence.newTitle')}
      size="lg"
      onClose={onClose}
    >
      <DialogApply
        label={editing ? t('common.save') : t('recurrence.create')}
        disabled={!canSave}
        pending={save.isPending}
        form={formId}
      />
      <form
        id={formId}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSave) save.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-xs">
          {t('recurrence.titleLabel')}
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t('recurrence.titlePlaceholder')}
            maxLength={500}
            autoFocus
          />
        </label>

        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-xs">
            {t('taskForm.type')}
            <select
              value={effectiveTypeId}
              onChange={(event) => setTaskTypeId(event.target.value)}
              className={FIELD}
            >
              {(types.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>

          <AssigneePicker
            members={members.data ?? []}
            value={assigneeId}
            onChange={setAssigneeId}
          />

          <label className="flex flex-col gap-1 text-xs">
            {t('taskForm.priority')}
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
              className={FIELD}
            >
              {TaskPriority.options.map((value) => (
                <option key={value} value={value}>
                  {t(`taskForm.priorityName.${value}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs">
            {t('taskForm.estimate')}
            <input
              type="number"
              min={0}
              step={0.5}
              value={estimate}
              onChange={(event) => setEstimate(event.target.value)}
              className={cn(FIELD, 'w-24')}
            />
          </label>
        </div>

        <fieldset className="flex flex-col gap-3 rounded-DEFAULT border border-border p-3">
          <legend className="px-1 text-xs font-medium">{t('recurrence.scheduleLabel')}</legend>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs">
              {t('recurrence.freqLabel')}
              <select
                value={freq}
                onChange={(event) => setFreq(event.target.value as RecurrenceFreq)}
                className={FIELD}
              >
                {FREQS.map((value) => (
                  <option key={value} value={value}>
                    {t(`recurrence.freq.${value}`)}
                  </option>
                ))}
              </select>
            </label>

            {freq === 'monthly' ? (
              <label className="flex flex-col gap-1 text-xs">
                {t('recurrence.monthDayLabel')}
                <select
                  value={monthDay}
                  onChange={(event) => setMonthDay(event.target.value)}
                  className={FIELD}
                >
                  {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                  <option value={0}>{t('recurrence.lastDay')}</option>
                </select>
              </label>
            ) : null}

            {freq === 'interval' ? (
              <label className="flex flex-col gap-1 text-xs">
                {t('recurrence.intervalLabel')}
                <input
                  type="number"
                  min={2}
                  max={366}
                  value={intervalDays}
                  onChange={(event) => setIntervalDays(event.target.value)}
                  className={cn(FIELD, 'w-24')}
                />
              </label>
            ) : null}

            <label className="flex flex-col gap-1 text-xs">
              {t('recurrence.startLabel')}
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className={FIELD}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t('recurrence.endLabel')}
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className={FIELD}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t('recurrence.leadLabel')}
              <input
                type="number"
                min={0}
                max={60}
                value={leadDays}
                onChange={(event) => setLeadDays(event.target.value)}
                className={cn(FIELD, 'w-24')}
              />
            </label>
          </div>

          {freq === 'weekly' ? (
            <div
              role="group"
              aria-label={t('recurrence.weekdaysLabel')}
              className="flex flex-wrap gap-1"
            >
              {WEEKDAY_ORDER.map((day) => {
                const on = weekdays.includes(day);
                return (
                  <Button
                    key={day}
                    type="button"
                    size="sm"
                    variant={on ? 'default' : 'outline'}
                    aria-pressed={on}
                    onClick={() => toggleWeekday(day)}
                  >
                    {t(`recurrence.weekdayShort.${day}`)}
                  </Button>
                );
              })}
            </div>
          ) : null}

          <p className="text-xs text-muted-foreground">{t('recurrence.leadHelp')}</p>

          <div className="text-xs" aria-live="polite">
            <span className="font-medium">{t('recurrence.previewLabel')}</span>{' '}
            {rule.success ? (
              preview.length > 0 ? (
                <span className="tabular-nums">{preview.map(displayDate).join(' · ')}</span>
              ) : (
                <span className="text-muted-foreground">{t('recurrence.previewNone')}</span>
              )
            ) : (
              <span className="text-destructive">
                {rule.error.issues[0]?.message ?? t('recurrence.previewInvalid')}
              </span>
            )}
            <p className="text-muted-foreground">{t('recurrence.previewHelp')}</p>
          </div>
        </fieldset>

        <div className="flex flex-col gap-2">
          {checklist === null ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => setChecklist([{ id: randomUuid(), text: '', weight: null }])}
            >
              <ListChecks aria-hidden />
              {t('recurrence.addChecklist')}
            </Button>
          ) : (
            <TaskChecklistEditor items={checklist} onChange={setChecklist} />
          )}
        </div>

        {editing ? (
          <p className="text-xs text-muted-foreground">{t('recurrence.editHelp')}</p>
        ) : null}
      </form>
    </Dialog>
  );
}
